import React, { useState, useLayoutEffect, useRef, useCallback } from "react";

export interface ResponsiveD3ContainerProps {
  children?: React.ReactNode;
  width?: string | number;
  height?: string | number;
  debounceTime?: number;
}

/** Responsive container for D3 charts (measures width and passes dimensions) */
const ResponsiveD3Container: React.FC<ResponsiveD3ContainerProps> = ({
  children,
  width = "100%",
  height = 380,
  debounceTime = 100,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const [dimensions, setDimensions] = useState<{ width: number | null; height: number }>({
    width: null,
    height: typeof height === "number" ? height : 380,
  });

  const updateDimensions = useCallback(() => {
    if (!containerRef.current) return;

    const { width: containerWidth } = containerRef.current.getBoundingClientRect();
    const newHeight = typeof height === "number" ? height : 380;
    const measuredWidth = containerWidth ? Math.round(containerWidth) : null;

    setDimensions((prevDims) => {
      if (prevDims.width === measuredWidth && prevDims.height === newHeight) {
        return prevDims;
      }
      return { width: measuredWidth, height: newHeight };
    });
  }, [height]);

  const handleResize = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(updateDimensions, debounceTime);
  }, [debounceTime, updateDimensions]);

  useLayoutEffect(() => {
    updateDimensions();
    window.addEventListener("resize", handleResize);

    if ((window as any).ResizeObserver && containerRef.current) {
      resizeObserverRef.current = new ResizeObserver(handleResize);
      resizeObserverRef.current.observe(containerRef.current);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      window.removeEventListener("resize", handleResize);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [debounceTime, handleResize, updateDimensions]);

  const containerStyle: React.CSSProperties = {
    width: typeof width === "string" ? width : `${width}px`,
    height: typeof height === "string" ? height : `${height}px`,
    minHeight: typeof height === "number" ? `${height}px` : (height as string),
  };

  return (
    <div ref={containerRef} className="d3-chart-container" style={containerStyle}>
      {dimensions.width == null
        ? null
        : React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) return child;
            const anyChild = child as any;
            const nextProps = {
              ...(anyChild.props || {}),
              width: dimensions.width,
              height: dimensions.height,
            } as Record<string, any>;
            return React.cloneElement(anyChild, nextProps);
          })}
    </div>
  );
};

ResponsiveD3Container.displayName = "ResponsiveD3Container";

export default ResponsiveD3Container;
